// SECURITY: MAO / DealLens calculator constants (shared limits, timeouts, repair taxonomy)
// RISK: Magic numbers scattered across files increase maintenance burden and risk of inconsistency
// ASSUMPTION: All numeric limits are based on business requirements and UI/UX constraints

/**
 * Responsive breakpoints for media queries
 */
export const BREAKPOINTS = {
  MOBILE: 600,
  FAB: 959,
};

/**
 * Numeric limits for validation and UI constraints
 */
/** Platform access fee percent baked into combined agent commission rate. */
export const PAF_RATE = 1;

export const LIMITS = {
  MAX_STRUCTURES: 10,
  MAX_PRICE_PER_SQFT: 50000,
  MAX_DEAL_NOTES_LENGTH: 1000,
  DEAL_NOTES_WARNING_THRESHOLD: 900,
  MAX_HOA: 10000,
  MAX_WHOLESALE_FEE: 100000,
  MIN_COMP_SQFT: 1,
  // SECURITY: Limit offer message length to prevent DoS and oversized payloads (CWE-400)
  OFFER_MESSAGE_MAX_LENGTH: 2000,
  // SECURITY: Distribution free-text shown to investors should be bounded.
  DISTRIBUTION_INFO_MAX_LENGTH: 2000,
  // UX: Keep showing windows manageable in the PAF wizard.
  DISTRIBUTION_SHOWING_WINDOWS_MAX: 10,
  // UX: Keep title company concise and readable in modal/card surfaces.
  OFFER_TERMS_TITLE_MAX_LENGTH: 200,
  // UX: Additional inspection days are only for longer periods beyond base 1-5.
  OFFER_TERMS_ADDITIONAL_DAYS_MIN: 6,
  // UX: Keep additional inspection days in a realistic range.
  OFFER_TERMS_ADDITIONAL_DAYS_MAX: 30,
};

/**
 * Timeout values in milliseconds
 */
export const TIMEOUTS = {
  COMPS_AGENT_MS: 5 * 60 * 1000,
  AUTOSAVE_DEBOUNCE_MS: 800,
  STRATEGIES_POLL_MS: 5 * 60 * 1000,
};

/**
 * Pagination configuration
 */
export const PAGINATION = {
  COMPS_PER_PAGE: 4,
};

/**
 * Report status values for comps analysis
 */
export const REPORT_STATUS = {
  INITIAL: "initial",
  PROCESSING: "processing",
  FINAL: "final",
};

/**
 * Canonical strategy-mode identifiers used by MAO calculator and MAOC strategies map.
 * Keep these aligned with backend strategy_name enum values.
 */
export const CALCULATION_MODES = Object.freeze({
  FIX_AND_FLIP: "fix_and_flip",
  BRRRR: "brrrr",
  ADU_DADU: "adu_dadu",
  FIX_AND_LIST: "fix_and_list",
  SPLASH_AND_DASH: "splash_and_dash",
  BUY_HOLD_LONG_TERM_RENTAL: "buy_hold_long_term_rental",
  SHORT_TERM_RENTAL: "short_term_rental",
});

/**
 * Legacy short calculation modes from older MAOC payloads.
 * Read-only compatibility shim for existing persisted data.
 */
export const LEGACY_MODE_ALIASES = Object.freeze({
  fixflip: CALCULATION_MODES.FIX_AND_FLIP,
  splashanddash: CALCULATION_MODES.SPLASH_AND_DASH,
  adu: CALCULATION_MODES.ADU_DADU,
  buyhold: CALCULATION_MODES.BUY_HOLD_LONG_TERM_RENTAL,
});

/**
 * Valid canonical calculation modes for MAO calculator.
 * Keys mirror the exit-strategy agent string literals.
 */
export const VALID_CALCULATION_MODES = Object.freeze([
  CALCULATION_MODES.FIX_AND_FLIP,
  CALCULATION_MODES.BRRRR,
  CALCULATION_MODES.ADU_DADU,
  CALCULATION_MODES.FIX_AND_LIST,
  CALCULATION_MODES.SPLASH_AND_DASH,
  CALCULATION_MODES.BUY_HOLD_LONG_TERM_RENTAL,
  CALCULATION_MODES.SHORT_TERM_RENTAL,
]);

/**
 * Normalizes legacy/current calculation mode values into canonical enum values.
 * `wholesale` remains pass-through for legacy runtime behavior.
 *
 * @param {unknown} mode
 * @returns {string}
 */
export function normalizeCalculationMode(mode) {
  if (typeof mode !== "string") return CALCULATION_MODES.FIX_AND_FLIP;
  const trimmed = mode.trim();
  if (!trimmed) return CALCULATION_MODES.FIX_AND_FLIP;
  if (trimmed === "wholesale") return "wholesale";
  if (VALID_CALCULATION_MODES.includes(trimmed)) return trimmed;
  return LEGACY_MODE_ALIASES[trimmed] || CALCULATION_MODES.FIX_AND_FLIP;
}

/** Property attribute that remembers the calculator strategy selected for this property. */
export const SELECTED_EXIT_STRATEGY_ATTR = "selected_exit_strategy";

/**
 * Strategy to open for a property. Values outside the calculator list become Fix and Flip.
 *
 * @param {unknown} mode
 * @returns {string}
 */
export function resolveListedCalculationMode(mode) {
  const normalized = normalizeCalculationMode(mode);
  if (VALID_CALCULATION_MODES.includes(normalized)) return normalized;
  return CALCULATION_MODES.FIX_AND_FLIP;
}

/**
 * Exit strategy saved on the property, or null when unset.
 *
 * @param {object | null | undefined} property
 * @returns {string | null}
 */
export function readPropertySelectedExitStrategy(property) {
  const raw = property?.additional_attributes?.[SELECTED_EXIT_STRATEGY_ATTR];
  if (typeof raw !== "string" || !raw.trim()) return null;
  return resolveListedCalculationMode(raw);
}

/**
 * Rental-based strategies share the same rental income/financing inputs.
 *
 * @param {unknown} mode
 * @returns {boolean}
 */
export function isRentalCalculationMode(mode) {
  const normalized = normalizeCalculationMode(mode);
  return (
    normalized === CALCULATION_MODES.BUY_HOLD_LONG_TERM_RENTAL ||
    normalized === CALCULATION_MODES.BRRRR
  );
}

/**
 * Default values for new calculations
 */
export const DEFAULT_CALCULATION = {
  arv: 0,
  final_arv: 0,
  agentClosingCosts: 0,
  buyerClosingCosts: 0,
  sellingClosingCosts: 0,
  profit: 0,
  repairCosts: 0,
  holdingCosts: 0,
  wholesaleFee: 0,
  mao: 0,
  roi: 0,
  loanAmount: 0,
  hoa: 0,
  utilities: 0,
  propertyTax: 0,
  monthlyInterest: 0,
  totalInterest: 0,
  ask_to_offer_rate: 0,
  listing_price: 0,
};

/**
 * Default user preferences for MAO calculator
 */
export const DEFAULT_PREFERENCES = {
  buyingAgentRate: 2,
  sellingAgentRate: 2,
  buyerClosingRate: 1.5,
  sellingClosingRate: 2.5,
  profitRate: 15,
  holdingPeriod: 12,
  hoa: 0,
  utilities: 0,
  propertyTax: 0,
  wholesaleFee: 0,
  interestRate: 10,
  quickRepairCost: 50,
  downPaymentPercent: 20,
};

/**
 * Slider configuration for various input fields
 */
export const SLIDER_CONFIG = {
  QUICK_REPAIR: {
    min: 0,
    max: 1000,
    step: 1,
    marks: [
      { value: 0, label: "$0" },
      { value: 1000, label: "$1000" },
    ],
  },
};

/**
 * Validation ranges for preferences form
 */
export const PREFERENCES_VALIDATION = {
  buyingAgentRate: { min: 0, max: 10, label: "Buying Agent Rate" },
  sellingAgentRate: { min: 0, max: 10, label: "Selling Agent Rate" },
  buyerClosingRate: { min: 0, max: 100, label: "Buyer Closing Rate" },
  sellingClosingRate: { min: 0, max: 100, label: "Selling Closing Rate" },
  profitRate: { min: 0, max: 100, label: "Profit Rate" },
  holdingPeriod: { min: 0, max: 60, label: "Holding Period" },
  hoa: { min: 0, max: 10000, label: "HOA" },
  interestRate: { min: 0, max: 100, label: "Interest Rate" },
  wholesaleFee: { min: 0, max: 100000, label: "Wholesale Fee" },
};

/**
 * Single source of truth for structure type allowlist (DRY).
 */
export const ALLOWED_STRUCTURE_TYPES = ["adu", "dadu", "cottage", "apartment", "condo"];

/**
 * Repair category definitions with sub-items for the repair form.
 */
export const REPAIR_CATEGORIES = [
  {
    type: "Roof",
    subItems: [
      { type: "Repair", hasEstimate: true },
      { type: "Replace", hasEstimate: true },
    ],
  },
  {
    type: "Paint",
    subItems: [
      { type: "Interior", hasEstimate: true },
      { type: "Exterior", hasEstimate: true },
    ],
  },
  {
    type: "Windows",
    subItems: [{ type: "Replace", hasEstimate: true, hasQuantity: true, maxQuantity: 25 }],
  },
  {
    type: "Landscaping",
    subItems: [
      { type: "Basic Cleanup", hasEstimate: true },
      { type: "Tree Removal", hasEstimate: true, hasQuantity: true, maxQuantity: 10 },
      { type: "Grading", hasEstimate: true },
      { type: "Irrigation System", hasEstimate: true },
      { type: "New Sod/Grass", hasEstimate: true },
      { type: "Hardscaping", hasEstimate: true },
    ],
  },
  {
    type: "Deck",
    subItems: [
      { type: "New", hasEstimate: true },
      { type: "Repair", hasEstimate: true },
    ],
  },
  {
    type: "Septic",
    subItems: [
      { type: "New Septic", hasEstimate: true },
      { type: "Tank only", hasEstimate: true },
      { type: "Leachfield only", hasEstimate: true },
    ],
  },
  {
    type: "Flooring",
    subItems: [
      { type: "Hardwood", hasEstimate: true },
      { type: "Vinyl", hasEstimate: true },
      { type: "Carpet", hasEstimate: true },
      { type: "Tile", hasEstimate: true },
    ],
  },
  {
    type: "Kitchen",
    subItems: [
      { type: "Full", hasEstimate: true },
      { type: "Appliances", hasEstimate: true },
      { type: "Cabinets", hasEstimate: true },
      { type: "Counters", hasEstimate: true },
    ],
  },
  {
    type: "Bathrooms",
    subItems: [
      { type: "Master", hasEstimate: true },
      { type: "Full", hasEstimate: true, hasQuantity: true, maxQuantity: 6 },
      { type: "Half", hasEstimate: true, hasQuantity: true, maxQuantity: 6 },
    ],
  },
  { type: "Foundation", subItems: [] },
  { type: "HVAC", subItems: [] },
  { type: "Plumbing", subItems: [] },
  { type: "Electrical", subItems: [] },
  { type: "Others", subItems: [] },
  { type: "SOW", subItems: [] },
];

/**
 * Shared input sx for Add Structure modal (DRY).
 */
export const STRUCTURE_FORM_INPUT_SX = {
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--input-outline-inactive)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--primary)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--primary)",
    boxShadow: "0 0 0 2px var(--focus-ring)",
  },
  "& .MuiInputBase-input::placeholder": { color: "#8F8F9F", opacity: 1 },
};
